// Drop-in shim mapping `<Image>` from `next/image` onto a plain `<img>`.
// Vite SPA = no SSR + no edge image optimization, so the optimized variant
// would only add complexity. We keep `width`, `height`, `alt`, `src`,
// `className`, `style`, `loading` — drop the rest silently (priority,
// placeholder, blurDataURL, fill, sizes, quality).
import type { ImgHTMLAttributes, ReactNode } from "react";

interface NextImageCompatProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string | { src: string };
  alt: string;
  width?: number | string;
  height?: number | string;
  priority?: boolean;
  fill?: boolean;
  placeholder?: string;
  blurDataURL?: string;
  quality?: number;
  sizes?: string;
  unoptimized?: boolean;
  children?: ReactNode;
}

export default function NextImageShim({
  src,
  alt,
  width,
  height,
  priority: _priority,
  fill,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  quality: _quality,
  sizes,
  unoptimized: _unoptimized,
  style,
  ...rest
}: NextImageCompatProps) {
  const resolvedSrc = typeof src === "string" ? src : src.src;
  const fillStyle = fill
    ? { position: "absolute" as const, inset: 0, width: "100%", height: "100%", ...style }
    : style;
  return (
    <img
      src={resolvedSrc}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      style={fillStyle}
      {...rest}
    />
  );
}
